import { createSelector } from 'reselect';
import {
  creditStatusTypes,
  REPORT_STATUS_AVAILABLE,
  REPORT_STATUS_IN_DISPUTE,
  REPORT_STATUS_MANUAL_REVIEW,
  REPORT_STATUS_NO_SSN_DOB,
  REPORT_STATUS_REGULATORY_RESTRICTIONS,
  REPORT_STATUS_SECURITY_FREEZE,
  RESPONSE_TYPE_GATEWAY_TIMEOUT,
  vantageScoreExclusionStatusTypes,
} from '../../constants/statusConstants';

export const allSharedReportsSelector = (state) => state.rentalApplication.currentApplication.sharedReports;

const getSharedReportsStatusesSelector = (applicantId) => (state) =>
  state.rentalApplication.currentApplication.sharedReportStatuses[applicantId];

const getSharedReportDataSelector = (applicantId) =>
  createSelector(allSharedReportsSelector, (sharedReports) => (sharedReports || {})[applicantId]);

export const getHasRetrievedSharedReportsSelector = (applicantId) =>
  createSelector(getSharedReportsStatusesSelector(applicantId), (sharedReportStatuses) =>
    Boolean(sharedReportStatuses),
  );

const getCreditReportStatusFromSnapshotSelector = (applicantId) => (state) =>
  (state.rentalApplication.currentApplication.applicantsData || []).find(({ userId }) => userId === applicantId)
    ?.creditInfo?.status;

export const getCreditReportStatusSelector = (applicantId) =>
  createSelector(
    getSharedReportsStatusesSelector(applicantId),
    getCreditReportStatusFromSnapshotSelector(applicantId),
    (sharedReportStatuses, creditReportStatusFromSnapshot) =>
      sharedReportStatuses?.creditReportStatus || creditReportStatusFromSnapshot,
  );

export const getIsCreditReportUnavailableSelector = (applicantId) =>
  createSelector(
    getCreditReportStatusSelector(applicantId),
    (creditReportStatus) =>
      Boolean(creditReportStatus) &&
      creditReportStatus !== REPORT_STATUS_AVAILABLE &&
      !vantageScoreExclusionStatusTypes.includes(creditReportStatus),
  );

export const getIsCreditReportNoRecordFoundSelector = (applicantId) =>
  createSelector(
    getCreditReportStatusSelector(applicantId),
    (creditReportStatus) => creditReportStatus === creditStatusTypes.NO_RECORD_FOUND,
  );

const getBackgroundReportStatusFromSnapshotSelector = (applicantId) => (state) =>
  (state.rentalApplication.currentApplication.applicantsData || []).find(({ userId }) => userId === applicantId)
    ?.backgroundInfo?.status;

export const getBackgroundReportStatusSelector = (applicantId) =>
  createSelector(
    getSharedReportsStatusesSelector(applicantId),
    getBackgroundReportStatusFromSnapshotSelector(applicantId),
    (sharedReportStatuses, backgroundReportStatusFromSnapshot) =>
      sharedReportStatuses?.backgroundReportStatus || backgroundReportStatusFromSnapshot,
  );

const getEvictionReportStatusSelector = (applicantId) =>
  createSelector(
    getSharedReportsStatusesSelector(applicantId),
    (sharedReportStatuses) => sharedReportStatuses?.evictionReportStatus,
  );

const getCriminalReportStatusSelector = (applicantId) =>
  createSelector(
    getSharedReportsStatusesSelector(applicantId),
    (sharedReportStatuses) => sharedReportStatuses?.criminalReportStatus,
  );

export const getAreCriminalReportsInDisputeSelector = (applicantId) =>
  createSelector(
    getCriminalReportStatusSelector(applicantId),
    (criminalReportStatus) => criminalReportStatus === REPORT_STATUS_IN_DISPUTE,
  );

export const getAreEvictionReportsInDisputeSelector = (applicantId) =>
  createSelector(
    getEvictionReportStatusSelector(applicantId),
    (evictionReportStatus) => evictionReportStatus === REPORT_STATUS_IN_DISPUTE,
  );

export const getAreCriminalReportsUnderManualReviewSelector = (applicantId) =>
  createSelector(
    getCriminalReportStatusSelector(applicantId),
    (criminalReportStatus) => criminalReportStatus === REPORT_STATUS_MANUAL_REVIEW,
  );

export const getAreEvictionReportsUnderManualReviewSelector = (applicantId) =>
  createSelector(
    getEvictionReportStatusSelector(applicantId),
    (evictionReportStatus) => evictionReportStatus === REPORT_STATUS_MANUAL_REVIEW,
  );

export const getAreCriminalReportsFrozenSelector = (applicantId) =>
  createSelector(
    getCriminalReportStatusSelector(applicantId),
    (criminalReportStatus) => criminalReportStatus === REPORT_STATUS_SECURITY_FREEZE,
  );

export const getAreEvictionReportsFrozenSelector = (applicantId) =>
  createSelector(
    getEvictionReportStatusSelector(applicantId),
    (evictionReportStatus) => evictionReportStatus === REPORT_STATUS_SECURITY_FREEZE,
  );

export const getAreCriminalReportsTemporarilyUnavailableSelector = (applicantId) =>
  createSelector(getCriminalReportStatusSelector(applicantId), (criminalReportStatus) =>
    [REPORT_STATUS_IN_DISPUTE, REPORT_STATUS_MANUAL_REVIEW, REPORT_STATUS_SECURITY_FREEZE].includes(
      criminalReportStatus,
    ),
  );

export const getAreEvictionReportsTemporarilyUnavailableSelector = (applicantId) =>
  createSelector(getEvictionReportStatusSelector(applicantId), (evictionReportStatus) =>
    [REPORT_STATUS_IN_DISPUTE, REPORT_STATUS_MANUAL_REVIEW, REPORT_STATUS_SECURITY_FREEZE].includes(
      evictionReportStatus,
    ),
  );

export const getAreCriminalReportsUnavailableSelector = (applicantId) =>
  createSelector(
    getCriminalReportStatusSelector(applicantId),
    (criminalReportStatus) =>
      Boolean(criminalReportStatus) &&
      criminalReportStatus !== REPORT_STATUS_AVAILABLE &&
      criminalReportStatus !== REPORT_STATUS_REGULATORY_RESTRICTIONS,
  );

export const getAreEvictionReportsUnavailableSelector = (applicantId) =>
  createSelector(
    getEvictionReportStatusSelector(applicantId),
    (evictionReportStatus) =>
      Boolean(evictionReportStatus) &&
      evictionReportStatus !== REPORT_STATUS_AVAILABLE &&
      evictionReportStatus !== REPORT_STATUS_REGULATORY_RESTRICTIONS,
  );

export const getIsBackgroundReportUnavailableDueToMissingSsnSelector = (applicantId) =>
  createSelector(
    getBackgroundReportStatusSelector(applicantId),
    (backgroundReportStatus) => backgroundReportStatus === REPORT_STATUS_NO_SSN_DOB,
  );

export const getIsBackgroundReportUnavailableSelector = (applicantId) =>
  createSelector(
    getBackgroundReportStatusSelector(applicantId),
    getAreCriminalReportsUnavailableSelector(applicantId),
    getAreEvictionReportsUnavailableSelector(applicantId),
    (backgroundReportStatus, areCriminalReportsUnavailable, areEvictionReportsUnavailable) =>
      (Boolean(backgroundReportStatus) && backgroundReportStatus !== REPORT_STATUS_AVAILABLE) ||
      // It's also unavailable if each individual record is unavailable.
      (areCriminalReportsUnavailable && areEvictionReportsUnavailable),
  );

export const getWasRetrieveSharedReportsGatewayTimeoutSelector = (applicantId) =>
  createSelector(
    getSharedReportDataSelector(applicantId),
    (reportData) => reportData?.responseType === RESPONSE_TYPE_GATEWAY_TIMEOUT,
  );
